/* =============================================================
   IncomingProfile.jsx — Detailed profile for Carlos Mendez
   -------------------------------------------------------------
   Hardcoded detailed profile page for the demo incoming student
   (Carlos Mendez). Shows the exchange progress steps, document
   checklist with upload/approve/reject, and admin notes.

   This is a purpose-built demo page. Other incoming students
   use GenericIncomingProfile.jsx instead.

   TODO (backend integration):
   - Merge with GenericIncomingProfile once data comes from Supabase
   - Replace localStorage status with a DB update
   ============================================================= */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useDocuments } from '../context/DocumentContext';
import { useLocalNote } from '../hooks/useLocalNote';

const STATUS_KEY   = 'unixplore_computed_status_incoming_carlos-mendez';
const STUDENT_ID   = 'carlos-mendez';
const STEP_STORAGE = `unixplore_step_${STUDENT_ID}`;

/* ─── STEPS & STATUS CONFIG ─────────────────────────────── */
const STEPS = [
  { label: 'Nomination',      sub: 'Until Apr'       },
  { label: 'Registration',    sub: 'Until 1 May'     },
  { label: 'Pre-arrival',     sub: '4–6 wks before'  },
  { label: 'During Exchange', sub: 'Semester'        },
  { label: 'Departure',       sub: 'End of semester' },
];

const STEP_NOTES = [
  'Awaiting official nomination from partner university IO.',
  'Student must complete ESMT registration and submit required documents.',
  'Verify all pre-arrival documents at least 4 weeks before arrival.',
  'Student has arrived — complete enrollment and monitor during semester.',
  'Close file, issue transcript, and send to home university.',
];

/* ─── BASE DOCUMENTS BY STEP ────────────────────────────── */
const BASE_DOCUMENTS = [
  // Step 0 — Nomination
  { name: 'Nomination Confirmation',      step: 0, icon: 'verified',          baseStatus: 'uploaded' },
  // Step 1 — Registration
  { name: 'Passport Copy',                step: 1, icon: 'draft',             baseStatus: 'uploaded' },
  { name: 'Application Form (ESMT)',       step: 1, icon: 'edit_note',         baseStatus: 'missing'  },
  { name: 'Official Transcript',           step: 1, icon: 'school',            baseStatus: 'pending'  },
  // Step 2 — Pre-arrival
  { name: 'Health Insurance Certificate', step: 2, icon: 'medical_services',  baseStatus: 'missing'  },
  { name: 'Visa / Residence Permit',      step: 2, icon: 'flight_takeoff',    baseStatus: 'missing'  },
  { name: 'Housing Confirmation',          step: 2, icon: 'real_estate_agent', baseStatus: 'missing'  },
  // Step 3 — During Exchange
  { name: 'Certificate of Arrival',        step: 3, icon: 'where_to_vote',    baseStatus: 'missing'  },
  { name: 'Enrollment Confirmation',        step: 3, icon: 'badge',            baseStatus: 'missing'  },
  // Step 4 — Departure
  { name: 'Certificate of Departure',      step: 4, icon: 'logout',           baseStatus: 'missing'  },
  { name: 'Transcript of Records',         step: 4, icon: 'description',      baseStatus: 'missing'  },
];

function statusConfig(status) {
  switch (status) {
    case 'uploaded':
    case 'approved':  return { label: 'Approved',          style: 'bg-emerald-100 text-emerald-700', icon: 'check_circle'    };
    case 'pending':   return { label: 'Pending Review',    style: 'bg-amber-100 text-amber-700',     icon: 'hourglass_empty' };
    case 'rejected':  return { label: 'Changes Requested', style: 'bg-red-100 text-red-600',         icon: 'cancel'          };
    default:          return { label: 'Missing',           style: 'bg-red-100 text-red-700',         icon: 'cancel'          };
  }
}

/* ─── HORIZONTAL STEPPER COMPONENT ─────────────────────── */
function HorizontalStepper({ steps, currentIndex, children }) {
  const n         = steps.length;
  const halfCell  = 100 / (2 * n);
  const lineSpan  = 100 - 2 * halfCell;
  const progFrac  = currentIndex <= 0 ? 0 : currentIndex / (n - 1);
  const progWidth = progFrac * lineSpan;
  const pct       = Math.round(progFrac * 100);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-[18px] text-indigo-600">route</span>
          <h2 className="font-display text-base font-semibold text-[#0b1c30]">Process Status</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">{currentIndex} of {n} phases completed</span>
          <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">{pct}%</span>
        </div>
      </div>

      <div className="relative flex">
        <div className="absolute bg-slate-100 rounded-full"
          style={{ top: '16px', left: `${halfCell}%`, width: `${lineSpan}%`, height: '2px' }} />
        {progWidth > 0 && (
          <div className="absolute rounded-full transition-all duration-500 overflow-hidden"
            style={{ top: '16px', left: `${halfCell}%`, width: `${progWidth}%`, height: '2px', backgroundColor: '#4343d5' }}>
            <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.9) 50%, transparent 100%)',
              width: '40%',
              animation: 'progress-shimmer 2.8s ease-in-out infinite',
            }} />
          </div>
        )}
        <style>{`
          @keyframes progress-shimmer {
            0%   { transform: translateX(-100%); }
            100% { transform: translateX(350%); }
          }
        `}</style>
        {steps.map((step, i) => {
          const isDone    = i < currentIndex;
          const isCurrent = i === currentIndex;
          return (
            <div key={i} className="flex-1 flex flex-col items-center">
              <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                isDone    ? 'bg-indigo-600 border-indigo-600' :
                isCurrent ? 'bg-white border-indigo-600'      :
                            'bg-white border-slate-200'
              }`}>
                {isDone ? (
                  <span className="material-symbols-outlined text-white text-[14px]">check</span>
                ) : isCurrent ? (
                  <>
                    <span className="absolute inset-0 rounded-full bg-indigo-400 animate-ping opacity-30" />
                    <div className="w-3 h-3 bg-indigo-600 rounded-full" />
                  </>
                ) : (
                  <span className="text-[10px] font-bold text-slate-300">{i + 1}</span>
                )}
              </div>
              <div className="mt-2 text-center px-0.5 w-full">
                <p className={`text-[11px] font-semibold leading-tight ${
                  isDone ? 'text-slate-600' : isCurrent ? 'text-indigo-700' : 'text-slate-300'
                }`}>{step.label}</p>
                <p className={`text-[10px] mt-0.5 leading-tight ${isCurrent ? 'text-indigo-500 font-medium' : 'text-slate-300'}`}>
                  {isCurrent ? '● Current' : step.sub}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {children && <div className="mt-5 space-y-2">{children}</div>}
    </div>
  );
}

/* ─── INCOMING PROFILE COMPONENT ───────────────────────── */
export default function IncomingProfile() {
  const navigate = useNavigate();
  const { getUpload, uploadDocument, approveDocument, rejectDocument, viewDocument } = useDocuments();
  const { note, setNote, save, savedAt, justSaved } = useLocalNote(STUDENT_ID);

  const [manualStep, setManualStep] = useState(() => {
    const saved = localStorage.getItem(STEP_STORAGE);
    return saved !== null ? parseInt(saved, 10) : null;
  });

  const currentStepIndex = manualStep ?? 2;
  const canAdvance = currentStepIndex < STEPS.length - 1;

  const advanceStep = () => {
    const next = currentStepIndex + 1;
    if (next >= STEPS.length) return;
    localStorage.setItem(STEP_STORAGE, next);
    setManualStep(next);
  };

  /* Build docs with effective status from DocumentContext */
  const docs = BASE_DOCUMENTS.map(doc => {
    const upload = getUpload(STUDENT_ID, doc.name);
    const effectiveStatus = upload ? upload.status : doc.baseStatus;
    return { ...doc, effectiveStatus, upload };
  });

  const approvedCount   = docs.filter(d => ['uploaded', 'approved'].includes(d.effectiveStatus)).length;
  const allDocsApproved = docs.every(d => ['uploaded', 'approved'].includes(d.effectiveStatus));
  const pendingCount    = docs.filter(d => d.effectiveStatus === 'pending').length;

  /* Computed status from docs */
  const computedStatus = (() => {
    if (allDocsApproved) return 'Visa Confirmed';
    const anyActive = docs.some(d => ['uploaded', 'approved', 'pending'].includes(d.effectiveStatus));
    if (anyActive) return 'Documents Pending';
    return 'Nominated';
  })();

  useEffect(() => {
    localStorage.setItem(STATUS_KEY, computedStatus);
  }, [computedStatus]);

  /* Group docs by step */
  const docsByStep = STEPS.map((step, i) => ({
    stepIndex: i,
    stepLabel: step.label,
    docs: docs.filter(d => d.step === i),
  }));

  const note_text = STEP_NOTES[currentStepIndex];

  return (
    <Layout>
      {/* Back */}
      <button onClick={() => navigate('/incoming')}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-600 mb-6 transition-colors group">
        <span className="material-symbols-outlined text-[18px] group-hover:-translate-x-0.5 transition-transform">arrow_back</span>
        Incoming Students
      </button>

      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="font-display text-4xl font-bold text-[#0b1c30]">Carlos Mendez</h1>
          <p className="text-[#464555] mt-2 flex items-center gap-1.5">
            <span className="material-symbols-outlined text-[16px]">apartment</span>
            IE University, Spain
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1 rounded-full border border-slate-200 text-sm text-slate-600 font-medium">Exchange 2026/27</span>
          <span className="px-3 py-1 rounded-full border border-slate-200 text-sm text-slate-600 font-medium">MIM</span>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
            computedStatus === 'Visa Confirmed'    ? 'bg-blue-100 text-blue-700'    :
            computedStatus === 'Documents Pending' ? 'bg-amber-100 text-amber-700'  :
                                                     'bg-slate-100 text-slate-600'
          }`}>
            {computedStatus}
          </span>
        </div>
      </div>

      {/* Stepper */}
      <HorizontalStepper steps={STEPS} currentIndex={currentStepIndex}>
        {note_text && (
          <div className="flex items-start gap-2.5 px-4 py-3 bg-indigo-50 rounded-xl border border-indigo-100">
            <span className="material-symbols-outlined text-indigo-500 text-[16px] flex-shrink-0 mt-0.5">info</span>
            <p className="text-xs text-indigo-700 leading-relaxed">
              <span className="font-semibold">{STEPS[currentStepIndex].label}: </span>{note_text}
            </p>
          </div>
        )}
        {allDocsApproved && currentStepIndex === 2 && (
          <div className="flex items-center justify-between px-4 py-3 bg-emerald-50 rounded-xl border border-emerald-200">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-emerald-500 text-[16px]">check_circle</span>
              <p className="text-xs font-semibold text-emerald-700">All documents approved — ready to advance</p>
            </div>
            <button onClick={advanceStep}
              className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-3 py-1.5 rounded-lg hover:bg-emerald-200 transition-colors flex items-center gap-1">
              Advance <span className="material-symbols-outlined text-[14px]">arrow_forward</span>
            </button>
          </div>
        )}
        {canAdvance && !(allDocsApproved && currentStepIndex === 2) && (
          <button onClick={advanceStep}
            className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-indigo-200 text-indigo-600 hover:bg-indigo-50 transition-colors">
            <span className="text-xs font-semibold flex items-center gap-2">
              <span className="material-symbols-outlined text-[15px]">arrow_circle_right</span>
              Mark as: <span className="text-indigo-800">{STEPS[currentStepIndex + 1]?.label}</span>
            </span>
            <span className="material-symbols-outlined text-[16px]">chevron_right</span>
          </button>
        )}
        {!canAdvance && (
          <div className="flex items-center gap-2.5 px-4 py-3 bg-emerald-50 rounded-xl border border-emerald-200">
            <span className="material-symbols-outlined text-emerald-500 text-[16px]">verified</span>
            <p className="text-xs font-semibold text-emerald-700">Exchange completed — all phases done.</p>
          </div>
        )}
      </HorizontalStepper>

      {/* Two-column content */}
      <div className="grid grid-cols-12 gap-6">

        {/* Left */}
        <div className="col-span-8 space-y-6">

          {/* Student Information */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px] text-indigo-600">person</span>
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">Student Information</h2>
            </div>
            <div className="p-6 grid grid-cols-2 gap-x-8 gap-y-5">
              <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Full Name</p><p className="text-sm text-[#0b1c30] font-medium">Carlos Mendez</p></div>
              <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Home University</p><p className="text-sm text-[#0b1c30] font-medium">IE University</p></div>
              <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Nationality</p><p className="text-sm text-[#0b1c30] font-medium">🇪🇸 Spain</p></div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Program</p>
                <span className="inline-block text-xs font-semibold bg-indigo-100 text-indigo-700 px-2.5 py-1 rounded-full">Master in Management</span>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Exchange Period</p>
                <p className="text-sm text-[#0b1c30] font-medium flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-[14px] text-slate-400">calendar_month</span>
                  Sep 2026 — Feb 2027
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Email</p>
                <a href="mailto:c.mendez@ie.edu" className="text-sm text-indigo-600 font-medium hover:underline flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-[14px]">mail</span>c.mendez@ie.edu
                </a>
              </div>
            </div>
          </div>

          {/* Documents — grouped by step */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-[18px] text-indigo-600">folder_open</span>
                <h2 className="font-display text-base font-semibold text-[#0b1c30]">Documents</h2>
              </div>
              <div className="flex items-center gap-2">
                {pendingCount > 0 && (
                  <span className="flex items-center gap-1 text-xs font-semibold bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full">
                    <span className="material-symbols-outlined text-[13px]">notifications</span>
                    {pendingCount} pending review
                  </span>
                )}
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                  allDocsApproved ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'
                }`}>
                  {approvedCount} of {docs.length} Complete
                </span>
              </div>
            </div>

            <div className="p-4 space-y-1">
              {docsByStep.map(group => {
                const isLocked  = group.stepIndex > currentStepIndex;
                const isCurrent = group.stepIndex === currentStepIndex;
                const isDone    = group.stepIndex < currentStepIndex;

                return (
                  <div key={group.stepIndex}>
                    {/* Step header */}
                    <div className={`flex items-center gap-2 px-2 pt-4 pb-2 ${group.stepIndex === 0 ? 'pt-2' : ''}`}>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        isDone    ? 'bg-indigo-100 text-indigo-700' :
                        isCurrent ? 'bg-indigo-600 text-white'      :
                                    'bg-slate-100 text-slate-400'
                      }`}>Step {group.stepIndex + 1}</span>
                      <span className={`text-xs font-semibold ${
                        isDone ? 'text-slate-500' : isCurrent ? 'text-indigo-700' : 'text-slate-300'
                      }`}>{group.stepLabel}</span>
                      {isDone && <span className="material-symbols-outlined text-indigo-400 text-[14px]">check_circle</span>}
                      {isLocked && <span className="material-symbols-outlined text-slate-300 text-[14px]">lock</span>}
                    </div>

                    {/* Doc rows */}
                    {group.docs.map(doc => {
                      const cfg       = statusConfig(doc.effectiveStatus);
                      const isPending = doc.effectiveStatus === 'pending';
                      return (
                        <div key={doc.name}
                          className={`flex items-center justify-between px-4 py-3.5 rounded-xl mx-2 mb-1 transition-colors ${
                            isLocked  ? 'opacity-40' :
                            isPending ? 'bg-amber-50 border border-amber-100' :
                                        'hover:bg-slate-50'
                          }`}>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                              <span className="material-symbols-outlined text-[16px] text-slate-500">{doc.icon}</span>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-[#0b1c30]">{doc.name}</span>
                              {isPending && doc.upload && (
                                <p className="text-xs text-amber-600 mt-0.5 flex items-center gap-1">
                                  <span className="material-symbols-outlined text-[11px]">attach_file</span>
                                  {doc.upload.fileName} · uploaded {new Date(doc.upload.uploadedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${cfg.style}`}>
                              <span className="material-symbols-outlined text-[14px]">{cfg.icon}</span>
                              {cfg.label}
                            </div>
                            {(doc.effectiveStatus === 'missing' || doc.effectiveStatus === 'rejected') && !isLocked && (
                              <label className="cursor-pointer text-xs font-medium text-indigo-600 border border-indigo-200 px-2.5 py-1.5 rounded-lg hover:bg-indigo-50 transition-colors flex items-center gap-1">
                                <span className="material-symbols-outlined text-[14px]">upload</span>Upload
                                <input type="file" className="hidden" onChange={e => {
                                  const file = e.target.files[0];
                                  if (file) uploadDocument(STUDENT_ID, 'Carlos Mendez', doc.name, file);
                                }} />
                              </label>
                            )}
                            {isPending && doc.upload && !isLocked && (
                              <>
                                <button onClick={() => viewDocument(doc.upload)}
                                  className="text-xs font-medium text-slate-600 border border-slate-200 px-2.5 py-1.5 rounded-lg hover:bg-slate-100 transition-colors flex items-center gap-1">
                                  <span className="material-symbols-outlined text-[14px]">open_in_new</span>View
                                </button>
                                <button onClick={() => approveDocument(doc.upload.id)}
                                  className="text-xs font-medium text-emerald-700 border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 rounded-lg hover:bg-emerald-100 transition-colors flex items-center gap-1">
                                  <span className="material-symbols-outlined text-[14px]">check</span>Approve
                                </button>
                                <button onClick={() => rejectDocument(doc.upload.id)}
                                  className="text-xs font-medium text-red-600 border border-red-200 bg-red-50 px-2.5 py-1.5 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-1">
                                  <span className="material-symbols-outlined text-[14px]">close</span>Reject
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right */}
        <div className="col-span-4 space-y-6">

          {/* Admin Notes */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-[18px] text-indigo-600">notes</span>
                <h2 className="font-display text-base font-semibold text-[#0b1c30]">Admin Notes</h2>
              </div>
              {savedAt && (
                <p className="text-[11px] text-slate-400 flex items-center gap-1">
                  <span className="material-symbols-outlined text-[12px]">schedule</span>
                  Saved {savedAt}
                </p>
              )}
            </div>
            <div className="p-5">
              <p className="text-xs text-slate-400 mb-3">Internal notes — visible only to university staff.</p>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                rows={4}
                placeholder="Add a note about this student's process..."
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 text-[#0b1c30] placeholder-slate-400 transition"
              />
              <button onClick={save}
                className={`mt-3 w-full py-2.5 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  justSaved ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'border border-slate-200 text-[#464555] hover:bg-slate-50'
                }`}>
                <span className="material-symbols-outlined text-[16px]">{justSaved ? 'check_circle' : 'save'}</span>
                {justSaved ? 'Saved!' : 'Save Note'}
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">Actions</h2>
            </div>
            <div className="p-5 space-y-3">
              <button className="w-full py-2.5 border border-indigo-300 text-indigo-600 rounded-xl text-sm font-medium hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2">
                <span className="material-symbols-outlined text-[16px]">notifications</span>Send Reminder
              </button>
              <button className="w-full py-2.5 bg-[#4343d5] text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2">
                <span className="material-symbols-outlined text-[16px]">mail</span>Contact Student
              </button>
            </div>
          </div>

          {/* IO Contacts */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px] text-indigo-600">contacts</span>
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">IO Contacts</h2>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">ESMT IO Manager</p>
                <p className="text-sm font-medium text-[#0b1c30]">Julia Klein</p>
                <a href="mailto:j.klein@esmt.org" className="text-xs text-indigo-600 hover:underline">j.klein@esmt.org</a>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Home University IO</p>
                <p className="text-sm font-medium text-[#0b1c30]">IE University — Int'l Office</p>
                <a href="mailto:international@ie.edu" className="text-xs text-indigo-600 hover:underline">international@ie.edu</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
